import { createContext, useContext, useEffect } from 'react'
import { EVERYONE } from './families'

// What the frame around a page needs to know from the page inside it: which family it belongs to (so the sidebar,
// the switcher and the phone's tabs point at that family), its title (which the phone's top bar shows on a tab's own
// page), where the phone's back button goes, the spot at the bar's right end where the page's "+" goes
// (CreateAction), and the middle of the phone's bar that a page's own top row moves up into (BarRow).
export type Page = { title?: string; back?: { to: string; label: string } }
export type Frame = {
  familyId: string | null
  setFamilyId: (id: string | null) => void
  page: Page
  setPage: (page: Page) => void
  actionSlot: HTMLElement | null
  setActionSlot: (slot: HTMLElement | null) => void
  rowSlot: HTMLElement | null
  setRowSlot: (slot: HTMLElement | null) => void
}

const KEY = 'gatheredfrom:family'
export const FrameContext = createContext<Frame | null>(null)

// The last family you were in is remembered on this device, so the app opens there. Everyone isn't a circle: which of
// the two the app opens on is the level, below.
export function lastFamily(): string | null {
  try {
    return localStorage.getItem(KEY)
  } catch {
    return null
  }
}

export const forgetFamily = () => remember(null)

export function remember(id: string | null) {
  if (id === EVERYONE) return
  try {
    if (id) localStorage.setItem(KEY, id)
    else localStorage.removeItem(KEY)
  } catch {
    // Private browsing can refuse storage; the app still works, it just won't remember.
  }
}

// Which level the app opens on, remembered on this device: Everyone, until you pick one of your circles from your
// profile; picking Everyone there again goes back to it (Dallas, 2026-10-17).
const LEVEL = 'gatheredfrom:level'
export function opensOnCircle(): boolean {
  try {
    return localStorage.getItem(LEVEL) === 'circle'
  } catch {
    return false
  }
}
export function chooseLevel(level: 'everyone' | 'circle') {
  try {
    localStorage.setItem(LEVEL, level)
  } catch {
    // Private browsing can refuse storage; the app opens on Everyone.
  }
}

export function useFrame(): Frame {
  const frame = useContext(FrameContext)
  if (!frame) throw new Error('useFrame needs a FrameProvider')
  return frame
}

// What an item's page needs to know to tell where it shows: the circle it was added in and who it's shared with.
export type Shown = { familyId: string; everyone?: boolean; sharedWith?: { familyId: string }[] }

// The place an album, recipe, moment or book's page sits in: the one you opened it from, when it shows there (it's that
// circle's own, it's shared into it, or it's for Everyone), so opening something from a circle never moves you into
// the circle it was added in. From a link, or from a place it doesn't show in, it sits in its own circle.
export function itemHome(current: string | null, item: Shown, ...more: Shown[]): string {
  const own = item.familyId
  if (!current || current === own || current === EVERYONE) return current ?? own
  const shows = [item, ...more].some((x) => x.everyone || x.sharedWith?.some((y) => y.familyId === current))
  return shows ? current : own
}

export const useItemHome = (item: Shown, ...more: Shown[]) => itemHome(useFrame().familyId, item, ...more)

// A page says which family it's in and what the phone's top bar should show for it.
export function usePageFrame(familyId: string | undefined, page: Page) {
  const { familyId: current, setFamilyId, setPage } = useFrame()
  const backTo = page.back?.to
  const backLabel = page.back?.label
  useEffect(() => {
    if (familyId && familyId !== current) setFamilyId(familyId)
  }, [familyId, current, setFamilyId])
  useEffect(() => {
    setPage({ title: page.title, back: backTo ? { to: backTo, label: backLabel ?? 'Back' } : undefined })
    return () => setPage({})
  }, [page.title, backTo, backLabel, setPage])
}
